import React, { Suspense, useCallback, useMemo } from 'react'
import { lazyComponent } from '../../../libs/onUnhandledPromiseRejection'
import { useLocation } from 'react-router-dom'
import { SidebarProvider, useSidebar } from '../../../shared/components/Sidebar'
import { MaterialSidebarLayout } from '../../../shared/components/Sidebar/MaterialSidebarLayout'
import { BuildingOffice2Icon, UserGroupIcon } from '@heroicons/react/24/outline'
import { SidebarSection, SharedSection, type SidebarNavigationItem } from '../../../shared/types/navigation'
import { FACILITATOR_FAQ_NAV_ITEM, FACILITATOR_NAVIGATION_ITEMS } from '../constants/navigation'
import { getSectionFromPath } from '../../../config/routes/routeConfig'
// Routes are imported from their own modules (never the ./index barrel) so each one is its own
// chunk — a barrel import pulls every page into the shell bundle and defeats the lazy loading below.
import { DashboardPage } from './DashboardPage'
import { ProfilePage } from './ProfilePage'
import { TrainingSprintManagementPage } from './TrainingSprintManagementPage'
import { UpcomingActivitiesFacilitatorPage } from './UpcomingActivitiesFacilitatorPage'

// Loaded on demand: these carry the heaviest dependencies (Unity WebGL, Whereby, Recharts, Draft.js,
// Lottie) or are rarely visited.
const SprintCreationPage = lazyComponent(() => import('./SprintCreationPage'), 'SprintCreationPage')
const SprintDetailsPage = lazyComponent(() => import('./SprintDetailsPage'), 'SprintDetailsPage')
const DownloadReportsPage = lazyComponent(() => import('./DownloadReportsPage'), 'DownloadReportsPage')
const ReportsManagementPage = lazyComponent(() => import('./ReportsManagementPage'), 'ReportsManagementPage')
const ReportOverviewPage = lazyComponent(() => import('./ReportOverviewPage'), 'ReportOverviewPage')
const ReflectionsManagementPage = lazyComponent(
  () => import('./ReflectionsManagementPage'),
  'ReflectionsManagementPage'
)
const FacilitateReflectionPage = lazyComponent(() => import('./FacilitateReflectionPage'), 'FacilitateReflectionPage')
const QAPage = lazyComponent(() => import('./QAPage'), 'QAPage')
const VoiceLottieTest = lazyComponent(() => import('./VoiceLottieTest'), 'VoiceLottieTest')
const BubbleLottieTest = lazyComponent(() => import('./BubbleLottieTest'), 'BubbleLottieTest')
const VoiceAnimationWithAudioOptimized = lazyComponent(
  () => import('./VoiceAnimationWithAudioOptimized'),
  'VoiceAnimationWithAudioOptimized'
)
const RechartsGlassTest = lazyComponent(() => import('./RechartsGlassTest'), 'RechartsGlassTest')
const CommunicationFeelingExamples = lazyComponent(
  () => import('./CommunicationFeelingExamples'),
  'CommunicationFeelingExamples'
)
const StackedChartsTest = lazyComponent(() => import('./StackedChartsTest'), 'StackedChartsTest')
const ComparisonBarChartDevTest = lazyComponent(
  () => import('./ComparisonBarChartDevTest'),
  'ComparisonBarChartDevTest'
)
const TeamHealthHeartDevTest = lazyComponent(() => import('./TeamHealthHeartDevTest'), 'TeamHealthHeartDevTest')
const GlintRevealDevTest = lazyComponent(() => import('./GlintRevealDevTest'), 'GlintRevealDevTest')
const GenerateReportVisualizationsDev = lazyComponent(
  () => import('./GenerateReportVisualizationsDev'),
  'GenerateReportVisualizationsDev'
)
const FunnelGraphDevTest = lazyComponent(() => import('./FunnelGraphDevTest'), 'FunnelGraphDevTest')
const FunnelGraphDevTestV2 = lazyComponent(() => import('./FunnelGraphDevTestV2'), 'FunnelGraphDevTestV2')
const FunnelGraphDevTestV3 = lazyComponent(() => import('./FunnelGraphDevTestV3'), 'FunnelGraphDevTestV3')
const TeamInformationSharesDevTest = lazyComponent(
  () => import('./TeamInformationSharesDevTest'),
  'TeamInformationSharesDevTest'
)
const PersonalityTraitsVisualizationDevTest = lazyComponent(
  () => import('./PersonalityTraitsVisualizationDevTest'),
  'PersonalityTraitsVisualizationDevTest'
)
const DevToolsShared = lazyComponent(() => import('../../shared/pages/DevToolsShared'), 'DevToolsShared')
const DevToolsAchievementsPage = lazyComponent(
  () => import('../../shared/pages/DevToolsAchievementsPage'),
  'DevToolsAchievementsPage'
)
const BOCleanupPage = lazyComponent(() => import('../../shared/pages/BOCleanupPage'), 'BOCleanupPage')
const PlayGameWithIntroductionPage = lazyComponent(
  () => import('../../gamer/pages/PlayGameWithIntroductionPage'),
  'PlayGameWithIntroductionPage'
)
const PlayGameWithoutIntroductionPage = lazyComponent(
  () => import('../../gamer/pages/PlayGameWithoutIntroductionPage'),
  'PlayGameWithoutIntroductionPage'
)
const AICoachPage = lazyComponent(() => import('../../gamer/pages/aiCoach'), 'AICoachPage')
const GameCenterFacilitatorPage = lazyComponent(
  () => import('../../gamer/pages/GameCenterFacilitatorPage'),
  'GameCenterFacilitatorPage'
)
const PermissionsInstructionsPage = lazyComponent(
  () => import('../../../shared/pages/PermissionsInstructionsPage'),
  'PermissionsInstructionsPage'
)
import { useCanAccessGenerateReports } from '../../../shared/hooks/useCanAccessGenerateReports'
import { RouteGuard } from '../../../components/RouteGuard'
import { LoadingPage } from '../../../components/LoadingPage'
import { useUpcomingActivities } from '../../../apiLayer/queryHooks/queries'
import { hasAnyLiveActivity } from '../../shared/helpers/liveActivityHelpers'
import { useQueryClient } from '@tanstack/react-query'
import { useFacilitatorActivitiesRealtimeSync } from '../hooks/useFacilitatorActivitiesRealtimeSync'
import { useActivityStatusTick } from '../../shared/hooks/useActivityStatusTick'
import { useActivityThresholdScheduler } from '../../shared/hooks/useActivityThresholdScheduler'
import { FACILITATOR_ACTIVITY_QUERY_KEYS } from '../../shared/constants/activitySync'
import { useAppDispatch, useAppSelector } from '../../../store'
import {
  buildFacilitatorSidebarNotificationCounts,
  getFacilitatorSidebarNotificationSection,
  shouldSurfaceFacilitatorSidebarNotification
} from '../utils/sidebarNotificationIndicators'
import { selectInAppNotificationsList } from '../../../store/selectors/inAppNotificationsSelectors'
import { processHandlers } from '../../../apiLayer/processHandlers'
import { markViewedLocally } from '../../../store/slices/inAppNotificationsSlice'
import { useOrgTenant } from '../../organization/OrgContext'
import { OrganizationSwitcher } from '../../organization/OrganizationSwitcher'
import { OrgTeamPage } from '../../organization/pages/OrgTeamPage'
import { OrgSettingsPage } from '../../organization/pages/OrgSettingsPage'

/**
 * Wrapper component for AI Coach page that extracts sessionId from URL path
 * Since we're using a wildcard route (/*), useParams won't work - we extract from pathname
 */
const AICoachPageWithSessionId: React.FC = () => {
  const location = useLocation()
  // Extract sessionId from path like /facilitator/ai-coach/sprintId_activityId_teamId
  const sessionIdMatch = location.pathname.match(/\/ai-coach\/([^/]+)/)
  const sessionId = sessionIdMatch ? sessionIdMatch[1] : undefined
  return <AICoachPage sessionId={sessionId} />
}

/**
 * Content renderer component that renders the appropriate page based on active section or route
 * Uses route config for clean section-to-component mapping
 */
const ContentRenderer: React.FC = () => {
  const { activeSection } = useSidebar()
  const location = useLocation()
  const { canAccessGenerateReports } = useCanAccessGenerateReports()

  // Get section from URL path using route config
  const sectionFromPath = getSectionFromPath(location.pathname, 'facilitator')
  const sectionValue = sectionFromPath || String(activeSection)

  // Extract sprintId from the URL path for sprint details
  const sprintIdMatch = location.pathname.match(/\/sprint\/([^/]+)/)
  const sprintId = sprintIdMatch ? sprintIdMatch[1] : null

  // Handle sprint details with dynamic ID - only if sprintId is valid
  if (sprintId && sprintId !== '' && sprintId !== 'undefined' && sprintId !== 'null') {
    return <SprintDetailsPage sprintId={sprintId} />
  }

  // If section is sprint-details but no valid ID, redirect to training sprints
  if (sectionValue === 'sprint-details' && !sprintId) {
  }

  // IMPORTANT: Check report overview route BEFORE switch statement to ensure correct routing
  // This handles finalized reports that should show preview (Step 5 + Step 4) instead of wizard flow
  if (location.pathname.includes('/generate-report/report/')) {
    if (!canAccessGenerateReports) {
      return <DashboardPage />
    }
    return <ReportOverviewPage />
  }

  // Map sections to components
  switch (sectionValue) {
    // Facilitator-specific sections
    case SidebarSection.DASHBOARD_FACILITATOR:
    case 'dashboard-facilitator':
      return <DashboardPage />
    case SidebarSection.PROFILE:
    case 'profile':
      return <ProfilePage />
    case 'permissions-instructions':
      return <PermissionsInstructionsPage />
    case SidebarSection.TRAINING_SPRINT_MANAGEMENT:
    case 'training-sprint-management':
      return <TrainingSprintManagementPage />
    case SidebarSection.SPRINT_CREATION:
    case 'sprint-creation':
      return <SprintCreationPage />
    case SidebarSection.DOWNLOAD_REPORTS:
    case 'download-reports':
    case 'manage-reports':
      if (!canAccessGenerateReports) {
        return <DashboardPage />
      }
      // Check if we're on the generate-report wizard route (for drafts/new reports)
      if (location.pathname.includes('/generate-report') && !location.pathname.includes('/report/')) {
        return <DownloadReportsPage />
      }
      return <ReportsManagementPage />
    case 'generate-report':
      if (!canAccessGenerateReports) {
        return <DashboardPage />
      }
      // Only show wizard for draft/new reports, not finalized report overviews
      if (location.pathname.includes('/report/')) {
        return <ReportOverviewPage />
      }
      return <DownloadReportsPage />
    case SidebarSection.REFLECTIONS:
    case 'reflections':
    case 'manage-reflections':
      return <ReflectionsManagementPage />
    case 'facilitate-reflection':
      return <FacilitateReflectionPage />

    case SidebarSection.ORGANIZATION_MEMBERS:
    case 'organization-members':
    case SidebarSection.ORGANIZATION_INVITES:
    case 'organization-invites':
      return <OrgTeamPage />
    case SidebarSection.ORGANIZATION_SETTINGS:
    case 'organization-settings':
      return <OrgSettingsPage />

    case SidebarSection.SESSIONS:
    case 'sessions':
    case 'upcoming-activities':
      return <UpcomingActivitiesFacilitatorPage />
    case SidebarSection.QA:
    case 'qa':
      return <QAPage />
    case SidebarSection.USERS:
    case 'users':
      // User management lives in the Back Office; /facilitator/back-office falls back to the dashboard.
      return <DashboardPage />

    // AI Coach - facilitator viewing a specific session
    case 'ai-coach':
      return <AICoachPageWithSessionId />

    // Shared sections - Game routes
    case SidebarSection.GAME_CENTER_FACILITATOR:
    case SharedSection.GAME_CENTER:
    case 'game-center':
    case 'game-center-facilitator':
      return <GameCenterFacilitatorPage />
    case SharedSection.PLAY_GAME_WITH_INTRODUCTION:
    case 'play-game-with-introduction':
      return <PlayGameWithIntroductionPage />
    case SharedSection.PLAY_GAME_WITHOUT_INTRODUCTION:
    case 'play-game-without-introduction':
      return <PlayGameWithoutIntroductionPage />

    // Facilitator-specific Dev tools
    case SharedSection.DEV_TOOLS:
    case 'dev-tools':
      return <DevToolsShared />
    case SharedSection.DEV_TOOLS_VOICE_LOTTIE:
    case 'dev-tools-voice-lottie':
      return <VoiceLottieTest />
    case SharedSection.DEV_TOOLS_BUBBLE_LOTTIE:
    case 'dev-tools-bubble-lottie':
      return <BubbleLottieTest />
    case SharedSection.DEV_TOOLS_VOICE_ANIMATION_AUDIO:
    case 'dev-tools-voice-animation-audio':
      return <VoiceAnimationWithAudioOptimized />
    case SharedSection.DEV_TOOLS_RECHARTS_GLASS:
    case 'dev-tools-recharts-glass':
      return <RechartsGlassTest />
    case SharedSection.DEV_TOOLS_COMMUNICATION_FEELING:
    case 'dev-tools-communication-feeling':
      return <CommunicationFeelingExamples />
    case SharedSection.DEV_TOOLS_STACKED_CHARTS:
    case 'dev-tools-stacked-charts':
      return <StackedChartsTest />
    case SharedSection.DEV_TOOLS_COMPARISON_BAR:
    case 'dev-tools-comparison-bar':
      return <ComparisonBarChartDevTest />
    case SharedSection.DEV_TOOLS_TEAM_HEALTH_HEART:
    case 'dev-tools-team-health-heart':
      return <TeamHealthHeartDevTest />
    case SharedSection.DEV_TOOLS_GLINT_REVEAL:
    case 'dev-tools-glint-reveal':
      return <GlintRevealDevTest />
    case SharedSection.DEV_TOOLS_GENERATE_REPORT_VISUALS:
    case 'dev-tools-generate-report-visuals':
      return <GenerateReportVisualizationsDev />
    case SharedSection.DEV_TOOLS_FUNNEL_GRAPH:
    case 'dev-tools-funnel-graph':
      return <FunnelGraphDevTest />
    case SharedSection.DEV_TOOLS_FUNNEL_GRAPH_V2:
    case 'dev-tools-funnel-graph-v2':
      return <FunnelGraphDevTestV2 />
    case SharedSection.DEV_TOOLS_FUNNEL_GRAPH_V3:
    case 'dev-tools-funnel-graph-v3':
      return <FunnelGraphDevTestV3 />
    case SharedSection.DEV_TOOLS_TEAM_INFORMATION_SHARES:
    case 'dev-tools-team-information-shares':
      return <TeamInformationSharesDevTest />
    case SharedSection.DEV_TOOLS_ACHIEVEMENTS:
    case 'dev-tools-achievements':
      return <DevToolsAchievementsPage />
    case SharedSection.DEV_TOOLS_PERSONALITY_TRAITS:
    case 'dev-tools-personality-traits':
      return <PersonalityTraitsVisualizationDevTest />
    case SharedSection.DEV_TOOLS_BO_CLEANUP:
    case 'dev-tools-bo-cleanup':
      return <BOCleanupPage />

    default:
      return <DashboardPage />
  }
}

/**
 * Main Facilitator Dashboard component with Material Tailwind sidebar navigation
 */
export const FacilitatorDashboard: React.FC = () => {
  const { isOrgAdmin, activeMembership, isPlatformSuper } = useOrgTenant()
  const [currentTimeMs, setCurrentTimeMs] = React.useState(() => Date.now())
  const location = useLocation()
  const { canAccessGenerateReports } = useCanAccessGenerateReports()
  const dispatch = useAppDispatch()
  const notifications = useAppSelector(selectInAppNotificationsList)
  React.useEffect(() => {
    const intervalId = window.setInterval(() => setCurrentTimeMs(Date.now()), 30_000)
    return () => window.clearInterval(intervalId)
  }, [])

  // Determine the default active section based on the current route using route config
  const sectionFromPath = getSectionFromPath(location.pathname, 'facilitator')
  const defaultActiveSection = (sectionFromPath as SidebarSection) || SidebarSection.DASHBOARD_FACILITATOR
  const sanitizedDefaultSection =
    !canAccessGenerateReports && defaultActiveSection === SidebarSection.DOWNLOAD_REPORTS
      ? SidebarSection.DASHBOARD_FACILITATOR
      : defaultActiveSection

  const navigationItems = React.useMemo(() => {
    const base = FACILITATOR_NAVIGATION_ITEMS.filter((item) => {
      if (item.id === SidebarSection.DOWNLOAD_REPORTS && !canAccessGenerateReports) {
        return false
      }
      return true
    })
    const backOfficeItem = base.find((item) => item.id === SidebarSection.USERS)
    const coreItems = base.filter((item) => item.id !== SidebarSection.USERS)
    const orgTeamItems: SidebarNavigationItem[] = activeMembership
      ? [
          {
            id: SidebarSection.ORGANIZATION_MEMBERS,
            label: 'org.navigation.team',
            icon: UserGroupIcon
          }
        ]
      : []
    const orgAdminItems: SidebarNavigationItem[] = isOrgAdmin
      ? [
          {
            id: SidebarSection.ORGANIZATION_SETTINGS,
            label: 'org.navigation.settings',
            icon: BuildingOffice2Icon
          }
        ]
      : []
    // Back office is platform-admin only — hide from org admins/facilitators.
    const backOfficeItems = backOfficeItem && isPlatformSuper ? [backOfficeItem] : []
    return [...coreItems, ...orgTeamItems, ...orgAdminItems, ...backOfficeItems, FACILITATOR_FAQ_NAV_ITEM]
  }, [canAccessGenerateReports, isOrgAdmin, activeMembership, isPlatformSuper])

  const queryClient = useQueryClient()
  const { data: activitiesResponse } = useUpcomingActivities()
  const activities = activitiesResponse?.data ?? []

  // Real-time sync: Firestore onSnapshot invalidates cache on any session change
  useFacilitatorActivitiesRealtimeSync()

  // Tick: re-evaluates time comparisons every 30s (fallback for the scheduler)
  const tick = useActivityStatusTick()

  // Scheduler: precise setTimeout at each activity's SOON/LIVE/END threshold
  const forceUpdate = useCallback(() => {
    FACILITATOR_ACTIVITY_QUERY_KEYS.forEach((key) => {
      queryClient.invalidateQueries({ queryKey: [key] })
    })
  }, [queryClient])
  useActivityThresholdScheduler(activities, forceUpdate)

  const hasLive = useMemo(
    () => hasAnyLiveActivity(activities),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activities, tick]
  )
  const liveActivitySectionIds = hasLive ? [SidebarSection.SESSIONS] : []
  const notificationCountBySection = React.useMemo(
    () => buildFacilitatorSidebarNotificationCounts(notifications, currentTimeMs),
    [notifications, currentTimeMs]
  )
  const activeNotificationSection = React.useMemo(() => {
    if (
      sectionFromPath === SidebarSection.SESSIONS ||
      sectionFromPath === 'sessions' ||
      sectionFromPath === 'upcoming-activities'
    ) {
      return SidebarSection.SESSIONS
    }

    if (
      sectionFromPath === SidebarSection.DOWNLOAD_REPORTS ||
      sectionFromPath === 'download-reports' ||
      sectionFromPath === 'manage-reports' ||
      sectionFromPath === 'generate-report'
    ) {
      return SidebarSection.DOWNLOAD_REPORTS
    }

    return null
  }, [sectionFromPath])
  const syncingNotificationIdsRef = React.useRef<Set<string>>(new Set())

  React.useEffect(() => {
    if (!activeNotificationSection) {
      return
    }

    const notificationsToMark = notifications.filter(
      (notification) =>
        shouldSurfaceFacilitatorSidebarNotification(notification, currentTimeMs) &&
        !syncingNotificationIdsRef.current.has(notification.id) &&
        getFacilitatorSidebarNotificationSection(notification) === activeNotificationSection
    )

    if (notificationsToMark.length === 0) {
      return
    }

    let cancelled = false
    notificationsToMark.forEach((notification) => syncingNotificationIdsRef.current.add(notification.id))

    void Promise.allSettled(
      notificationsToMark.map(async (notification) => {
        const result = await processHandlers.markNotificationViewed(notification.id)
        if (!result.success) {
          throw new Error(`markNotificationViewed failed for ${notification.id}`)
        }
        return notification.id
      })
    ).then((results) => {
      if (cancelled) {
        return
      }

      results.forEach((result, index) => {
        const notificationId = notificationsToMark[index].id
        syncingNotificationIdsRef.current.delete(notificationId)

        if (result.status === 'fulfilled') {
          dispatch(markViewedLocally(result.value))
          return
        }

        console.error('Failed to mark facilitator notification as viewed', notificationId, result.reason)
      })
    })

    return () => {
      cancelled = true
    }
  }, [activeNotificationSection, currentTimeMs, dispatch, notifications])

  return (
    <SidebarProvider navigationItems={navigationItems} defaultActiveSection={sanitizedDefaultSection}>
      <MaterialSidebarLayout
        profileTopSlot={<OrganizationSwitcher />}
        navigationItems={navigationItems}
        liveActivitySectionIds={liveActivitySectionIds}
        notificationCountBySection={notificationCountBySection}
      >
        <RouteGuard>
          <Suspense fallback={<LoadingPage />}>
            <ContentRenderer />
          </Suspense>
        </RouteGuard>
      </MaterialSidebarLayout>
    </SidebarProvider>
  )
}
